import * as THREE from 'three';

export interface CameraFramingControls {
  target: THREE.Vector3;
  update: () => void;
}

export interface CameraFramingViewport {
  width: number;
  height: number;
}

export interface CameraFramingOptions {
  padding?: number;
  upwardScreenShiftRatio?: number;
  zoomOutSteps?: number;
}

export interface CameraFramingResult {
  bounds: THREE.Box3;
  target: THREE.Vector3;
  projectedWidth: number;
  projectedHeight: number;
}

const DEFAULT_PADDING = 1.15;
const ORBIT_WHEEL_ZOOM_SCALE = 0.95;
const SNAP_ZERO_TOLERANCE = 0.08;
const SNAP_COMPONENT_TOLERANCE = 0.08;

function getBoxCorners(box: THREE.Box3): THREE.Vector3[] {
  const { min, max } = box;
  return [
    new THREE.Vector3(min.x, min.y, min.z),
    new THREE.Vector3(min.x, min.y, max.z),
    new THREE.Vector3(min.x, max.y, min.z),
    new THREE.Vector3(min.x, max.y, max.z),
    new THREE.Vector3(max.x, min.y, min.z),
    new THREE.Vector3(max.x, min.y, max.z),
    new THREE.Vector3(max.x, max.y, min.z),
    new THREE.Vector3(max.x, max.y, max.z),
  ];
}

export function getObjectsWorldBounds(objects: readonly THREE.Object3D[]): THREE.Box3 {
  const bounds = new THREE.Box3();

  objects.forEach((object) => {
    object.updateWorldMatrix(true, true);
    const objectBounds = new THREE.Box3().setFromObject(object, true);
    if (!objectBounds.isEmpty()) bounds.union(objectBounds);
  });

  return bounds;
}

export function isSnappedDiagonalDirection(direction: THREE.Vector3): boolean {
  if (direction.lengthSq() < 1e-8) return false;

  const components = direction.clone().normalize().toArray().map(Math.abs);
  const activeComponents = components.filter((value) => value > SNAP_ZERO_TOLERANCE);
  if (activeComponents.length < 2) return false;

  const expectedComponent = 1 / Math.sqrt(activeComponents.length);
  return components.every((value) =>
    value <= SNAP_ZERO_TOLERANCE
      || Math.abs(value - expectedComponent) <= SNAP_COMPONENT_TOLERANCE
  );
}

export function frameCameraToObjects(
  camera: THREE.Camera,
  controls: CameraFramingControls,
  viewport: CameraFramingViewport,
  objects: readonly THREE.Object3D[],
  options: CameraFramingOptions = {}
): CameraFramingResult | null {
  if (viewport.width <= 0 || viewport.height <= 0 || objects.length === 0) return null;

  const bounds = getObjectsWorldBounds(objects);
  if (bounds.isEmpty()) return null;

  camera.updateMatrixWorld(true);
  const cameraQuaternion = camera.getWorldQuaternion(new THREE.Quaternion());
  const right = new THREE.Vector3(1, 0, 0).applyQuaternion(cameraQuaternion).normalize();
  const up = new THREE.Vector3(0, 1, 0).applyQuaternion(cameraQuaternion).normalize();
  const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(cameraQuaternion).normalize();
  const center = bounds.getCenter(new THREE.Vector3());
  const corners = getBoxCorners(bounds);

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  corners.forEach((corner) => {
    const relative = corner.clone().sub(center);
    const x = relative.dot(right);
    const y = relative.dot(up);
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  });

  const projectedWidth = maxX - minX;
  const projectedHeight = maxY - minY;
  const padding = Math.max(options.padding ?? DEFAULT_PADDING, 1);
  const upwardShift = Math.max(options.upwardScreenShiftRatio ?? 0, 0);
  const zoomOutSteps = Math.max(options.zoomOutSteps ?? 0, 0);
  const wheelZoomScale = Math.pow(ORBIT_WHEEL_ZOOM_SCALE, zoomOutSteps);

  // Moving the camera and target down along the camera's up axis moves the
  // model upward on screen without changing the selected viewing direction.
  const target = center.clone().addScaledVector(up, -projectedHeight * upwardShift);
  const aspect = viewport.width / viewport.height;

  let maxForward = -Infinity;
  let minForward = Infinity;
  let perspectiveDistance = 0;

  if (camera instanceof THREE.PerspectiveCamera) {
    const verticalTangent = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
    const horizontalTangent = verticalTangent * aspect;

    corners.forEach((corner) => {
      const relative = corner.clone().sub(target);
      const x = relative.dot(right);
      const y = relative.dot(up);
      const z = relative.dot(forward);
      maxForward = Math.max(maxForward, z);
      minForward = Math.min(minForward, z);
      perspectiveDistance = Math.max(
        perspectiveDistance,
        Math.abs(x) * padding / horizontalTangent - z,
        Math.abs(y) * padding / verticalTangent - z
      );
    });

    const depth = Math.max(maxForward - minForward, 1);
    // The corner equations above already account for perspective depth. Only
    // keep the nearest point safely in front of the camera; adding the full
    // model depth here makes side and lower diagonal views unnecessarily tiny.
    const nearClearance = Math.max(depth * 0.02, 1);
    perspectiveDistance = Math.max(perspectiveDistance, -minForward + nearClearance, 1);
    perspectiveDistance /= wheelZoomScale;
    camera.position.copy(target).addScaledVector(forward, -perspectiveDistance);
    camera.aspect = aspect;
    camera.near = Math.max(0.1, perspectiveDistance - maxForward - depth);
    camera.far = Math.max(2000, perspectiveDistance - minForward + depth * 4);
    camera.updateProjectionMatrix();
  } else if (camera instanceof THREE.OrthographicCamera) {
    let requiredHalfWidth = 0;
    let requiredHalfHeight = 0;

    corners.forEach((corner) => {
      const relative = corner.clone().sub(target);
      const x = relative.dot(right);
      const y = relative.dot(up);
      const z = relative.dot(forward);
      requiredHalfWidth = Math.max(requiredHalfWidth, Math.abs(x) * padding);
      requiredHalfHeight = Math.max(requiredHalfHeight, Math.abs(y) * padding);
      maxForward = Math.max(maxForward, z);
      minForward = Math.min(minForward, z);
    });

    const halfHeight = Math.max(requiredHalfHeight, requiredHalfWidth / aspect, 0.5);
    const halfWidth = halfHeight * aspect;
    const depth = Math.max(maxForward - minForward, 1);
    const distance = Math.max(depth * 2, bounds.getSize(new THREE.Vector3()).length(), 1);

    camera.left = -halfWidth;
    camera.right = halfWidth;
    camera.top = halfHeight;
    camera.bottom = -halfHeight;
    camera.zoom = wheelZoomScale;
    camera.position.copy(target).addScaledVector(forward, -distance);
    camera.near = 0.1;
    camera.far = Math.max(2000, distance - minForward + depth * 4);
    camera.updateProjectionMatrix();
  } else {
    return null;
  }

  controls.target.copy(target);
  camera.lookAt(target);
  controls.update();
  camera.updateMatrixWorld(true);

  return { bounds, target, projectedWidth, projectedHeight };
}
